import { MOBILE_DUMMY_IMAGE_LIST, MOBILE_DUMMY_IMAGES } from "../mobileDummyImages";

const DAY_MS = 24 * 60 * 60 * 1000;

export const PHOTO_ARCHIVE_STATE = {
  AVAILABLE: "available",
  EXPIRING: "expiring",
  ARCHIVED: "archived",
  PROCESSING: "processing",
  PARTIAL: "partial",
  UNKNOWN: "unknown",
};

function toFiniteNumber(value) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue >= 0 ? numberValue : null;
}

export function parseArchiveDate(value) {
  if (!value) return null;
  const text = String(value).trim();
  if (!text) return null;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(text)
    ? new Date(`${text}T23:59:59+09:00`)
    : new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getDaysRemaining(value, now = new Date()) {
  const date = parseArchiveDate(value);
  if (!date) return null;
  const getSeoulDayNumber = (target) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Seoul",
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).formatToParts(target);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day)) / DAY_MS;
  };
  return getSeoulDayNumber(date) - getSeoulDayNumber(now);
}

export function formatArchiveDate(value, includeDay = false) {
  const date = parseArchiveDate(value);
  if (!date) return "";
  const formatter = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(includeDay ? { weekday: "short" } : {}),
  });
  return formatter.format(date).replace(/\. /g, ".").replace(/\.$/, "");
}

function addDays(date, amount) {
  return new Date(date.getTime() + amount * DAY_MS);
}

function toDateOnly(date) {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(date);
}

function getLegacyExpiry(uploadDate) {
  const date = parseArchiveDate(uploadDate);
  if (!date) return null;
  const expiry = new Date(date);
  expiry.setFullYear(expiry.getFullYear() + 1);
  expiry.setMonth(expiry.getMonth() + 6);
  return expiry.toISOString();
}

export function getRenditionUrl(image, size = "grid") {
  if (!image) return "";
  if (typeof image === "string") return image;

  const rendition = image.renditions?.[size] || image.renditions?.viewer || image.renditions?.grid;
  if (typeof rendition === "string") return rendition;
  if (rendition?.url) return rendition.url;

  if (size === "viewer") {
    return image.viewer_url
      || image.viewerUrl
      || image.middle_url
      || image.middle_img
      || image.grid_url
      || image.small_url
      || image.small_img
      || image.img_url
      || image.url
      || "";
  }

  return image.grid_url
    || image.gridUrl
    || image.small_url
    || image.small_img
    || image.middle_url
    || image.middle_img
    || image.img_url
    || image.url
    || "";
}

function uniqueImages(images = []) {
  const seen = new Set();
  return images.filter((image) => {
    const key = getRenditionUrl(image, "viewer") || getRenditionUrl(image, "grid");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getPreviewImages(row) {
  const plannedImages = row?.preview_images
    || row?.previewImages
    || row?.cover?.images
    || row?.thumbnails
    || [];
  const imageList = Array.isArray(plannedImages) ? plannedImages : [plannedImages];
  return uniqueImages([
    ...imageList,
    row?.cover,
    row?.thumbnail,
  ]);
}

function getExplicitPhotoCount(row) {
  return toFiniteNumber(
    row?.counts?.total
      ?? row?.photo_count
      ?? row?.total_photo_count
      ?? row?.image_count,
  );
}

function getOriginalExpiry(row, allowLegacyExpiry) {
  return row?.original_access?.retention_expires_at
    || row?.original_retention_expires_at
    || row?.next_expires_at
    || row?.download_expires_at
    || row?.original_expires_at
    || (allowLegacyExpiry ? getLegacyExpiry(row?.upload_date || row?.upload_at) : null);
}

function getOriginalDeletedAt(row) {
  return row?.original_access?.deleted_at
    || row?.original_deleted_at
    || row?.org_delete_dtm
    || null;
}

function getWebState(row, previewImages) {
  const rawState = String(
    row?.web_archive?.state
      || row?.web_preview_status
      || row?.preview_status
      || "",
  ).toUpperCase();
  if (["PENDING", "PROCESSING"].includes(rawState)) return "PENDING";
  if (rawState === "FAILED") return "FAILED";
  if (rawState === "PARTIAL") return "PARTIAL";
  if (rawState === "READY") return "READY";
  return previewImages.length > 0 ? "PARTIAL" : "PENDING";
}

function getDownloadPermission(row, expiryAt, deletedAt, now) {
  const actions = Array.isArray(row?.allowed_actions) ? row.allowed_actions : [];
  const explicitPermission = row?.original_access?.can_download ?? row?.can_download;
  if (deletedAt) return false;
  if (typeof explicitPermission === "boolean") return explicitPermission;
  if (actions.length > 0) return actions.includes("DOWNLOAD_ORIGINAL");
  const expiryDate = parseArchiveDate(expiryAt);
  if (expiryDate) return expiryDate.getTime() >= now.getTime();
  return Number(row?.new_download_file_count) > 0 ? true : null;
}

function getAlbumState({ webState, canDownload, expiryAt, deletedAt }, now) {
  if (canDownload === true) {
    const daysRemaining = getDaysRemaining(expiryAt, now);
    return daysRemaining !== null && daysRemaining <= 7
      ? PHOTO_ARCHIVE_STATE.EXPIRING
      : PHOTO_ARCHIVE_STATE.AVAILABLE;
  }
  if (["PENDING", "FAILED"].includes(webState)) return PHOTO_ARCHIVE_STATE.PROCESSING;
  if (webState === "PARTIAL") return PHOTO_ARCHIVE_STATE.PARTIAL;
  if (deletedAt || canDownload === false) return PHOTO_ARCHIVE_STATE.ARCHIVED;
  if (canDownload === null) return PHOTO_ARCHIVE_STATE.UNKNOWN;
  return PHOTO_ARCHIVE_STATE.UNKNOWN;
}

function normalizePhotoRow(row, index, { allowLegacyExpiry = false, now = new Date() } = {}) {
  const previewImages = getPreviewImages(row);
  const scheduleId = row?.schedule_id ?? row?.scheduleId ?? row?.album_id ?? index + 1;
  const brandId = row?.brand_id ?? "default";
  const expiryAt = getOriginalExpiry(row, allowLegacyExpiry);
  const deletedAt = getOriginalDeletedAt(row);
  const webState = getWebState(row, previewImages);
  const canDownload = getDownloadPermission(row, expiryAt, deletedAt, now);
  const albumState = getAlbumState({ webState, canDownload, expiryAt, deletedAt }, now);

  return {
    id: String(row?.album_id || `${brandId}:${scheduleId}`),
    deliveryId: String(row?.delivery_id || row?.deliveryId || row?.album_id || ""),
    brandId: String(brandId),
    scheduleId: String(scheduleId),
    title: row?.title || row?.photo_shoot_name || row?.package_name || "촬영 사진",
    photoType: row?.photo_type_nm || row?.photo_type || "본촬영",
    scheduleDate: row?.schedule_date || row?.scheduleDate || "",
    studioName: row?.office_name || row?.studio_name || "연결 스튜디오",
    uploadDate: row?.upload_date || row?.upload_at || "",
    photoCount: getExplicitPhotoCount(row),
    uploadCount: toFiniteNumber(row?.upload_batch_count) || 1,
    previewImages,
    expiryAt,
    deletedAt,
    canDownload,
    downloadableCount: toFiniteNumber(row?.original_access?.downloadable_count) ?? (canDownload === true ? 1 : 0),
    webState,
    state: albumState,
    originalDownloadUrl: row?.original_access?.download_url || row?.download_url || "",
    source: row?.source || "api",
  };
}

function mergeAlbums(current, next, now) {
  const previews = uniqueImages([...current.previewImages, ...next.previewImages]);
  const explicitCounts = [current.photoCount, next.photoCount].filter((value) => value !== null);
  const expiryCandidates = [current.expiryAt, next.expiryAt]
    .map((value) => ({ value, date: parseArchiveDate(value) }))
    .filter((item) => item.date)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
  const permissions = [current.canDownload, next.canDownload];
  const canDownload = permissions.includes(true)
    ? true
    : permissions.every((value) => value === false)
      ? false
      : null;
  const webState = [current.webState, next.webState].includes("FAILED")
    ? "PARTIAL"
    : [current.webState, next.webState].includes("PENDING")
      ? "PENDING"
      : [current.webState, next.webState].includes("PARTIAL")
        ? "PARTIAL"
        : "READY";
  const expiryAt = expiryCandidates[0]?.value || current.expiryAt || next.expiryAt;
  const deletedAt = canDownload === true ? null : current.deletedAt || next.deletedAt;

  return {
    ...current,
    title: current.title || next.title,
    scheduleDate: current.scheduleDate || next.scheduleDate,
    studioName: current.studioName || next.studioName,
    photoCount: explicitCounts.length > 0 ? Math.max(...explicitCounts) : null,
    uploadCount: current.uploadCount + next.uploadCount,
    previewImages: previews,
    expiryAt,
    deletedAt,
    canDownload,
    downloadableCount: current.downloadableCount + next.downloadableCount,
    webState,
    state: getAlbumState({ webState, canDownload, expiryAt, deletedAt }, now),
    originalDownloadUrl: current.originalDownloadUrl || next.originalDownloadUrl,
  };
}

export function normalizePhotoAlbums(rows = [], options = {}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const now = options.now || new Date();
  const albumMap = new Map();

  safeRows.forEach((row, index) => {
    const normalized = normalizePhotoRow(row, index, { ...options, now });
    const current = albumMap.get(normalized.id);
    albumMap.set(normalized.id, current ? mergeAlbums(current, normalized, now) : normalized);
  });

  return Array.from(albumMap.values())
    .sort((a, b) => String(b.scheduleDate).localeCompare(String(a.scheduleDate)));
}

export function normalizeDetailPhotos(photoDetailData, fallbackAlbum) {
  const folders = Array.isArray(photoDetailData?.img_list) ? photoDetailData.img_list : [];
  const photos = folders.flatMap((folder, folderIndex) => {
    const children = folder?.select_photo_list || folder?.photo_list || folder?.images || [];
    return (Array.isArray(children) ? children : []).map((photo, photoIndex) => {
      const hasExplicitWebRendition = Boolean(
        photo?.renditions
          || photo?.grid_url
          || photo?.gridUrl
          || photo?.viewer_url
          || photo?.viewerUrl
          || photo?.small_url
          || photo?.small_img
          || photo?.middle_url
          || photo?.middle_img,
      );
      const allowLegacyGenericUrl = fallbackAlbum?.canDownload !== false || hasExplicitWebRendition;
      const gridUrl = getRenditionUrl(photo, "grid");
      const viewerUrl = getRenditionUrl(photo, "viewer");

      return {
        id: String(photo?.photo_id || `${folder?.folder_id || folderIndex}-${photoIndex}`),
        gridUrl: allowLegacyGenericUrl ? gridUrl : "",
        viewerUrl: allowLegacyGenericUrl ? viewerUrl : "",
        fileName: photo?.file_name || `${fallbackAlbum?.title || "촬영 사진"} ${photoIndex + 1}`,
        folderName: folder?.folder_name || "촬영 사진",
      };
    });
  }).filter((photo) => photo.gridUrl || photo.viewerUrl);

  return photos;
}

export function normalizeFileListPhotos(photoFileListData) {
  const batches = Array.isArray(photoFileListData?.data) ? photoFileListData.data : [];
  return batches.flatMap((batch, batchIndex) => {
    const files = Array.isArray(batch?.upload_file_detail_data) ? batch.upload_file_detail_data : [];
    return files.map((file, fileIndex) => ({
      id: String(file?.upload_file_detail_pk || `${batch?.upload_file_pk || batchIndex}-${fileIndex}`),
      gridUrl: file?.small_img || file?.middle_img || "",
      viewerUrl: file?.middle_img || file?.small_img || "",
      fileName: file?.file_nm || `촬영 사진 ${fileIndex + 1}`,
      folderName: file?.folder_name || "촬영 사진",
      uploadBatchId: String(batch?.upload_file_pk || batchIndex),
    }));
  }).filter((photo) => photo.gridUrl || photo.viewerUrl);
}

export function getSamplePhotoRows(now = new Date()) {
  const availableExpiry = toDateOnly(addDays(now, 9));
  const deletedAt = addDays(now, -10).toISOString();

  return [
    {
      source: "sample",
      album_id: "sample-first-birthday",
      brand_id: 1,
      schedule_id: 2026062501,
      title: "첫돌 촬영",
      photo_type_nm: "본촬영",
      schedule_date: "2026-06-25",
      office_name: "MY STUDIO 강남점",
      upload_date: "2026-06-27",
      counts: { total: 326 },
      upload_batch_count: 1,
      preview_images: [
        MOBILE_DUMMY_IMAGES.birthdayPink,
        MOBILE_DUMMY_IMAGES.babyCrown,
        MOBILE_DUMMY_IMAGES.pureWindow,
      ],
      original_access: {
        can_download: true,
        retention_expires_at: availableExpiry,
        downloadable_count: 1,
      },
      web_archive: { state: "READY" },
    },
    {
      source: "sample",
      album_id: "sample-family",
      brand_id: 1,
      schedule_id: 2026031201,
      title: "가족 촬영",
      photo_type_nm: "본촬영",
      schedule_date: "2026-03-12",
      office_name: "MY STUDIO 강남점",
      upload_date: "2026-03-14",
      counts: { total: 182 },
      upload_batch_count: 2,
      preview_images: [
        MOBILE_DUMMY_IMAGES.familyGarden,
        MOBILE_DUMMY_IMAGES.playfulBaby,
        MOBILE_DUMMY_IMAGES.maternity,
      ],
      original_access: {
        can_download: false,
        deleted_at: deletedAt,
        downloadable_count: 0,
      },
      web_archive: { state: "READY" },
    },
  ];
}

export function getSampleAlbumPhotos(album) {
  const offset = String(album?.id || "").includes("family") ? 2 : 0;
  return Array.from({ length: 12 }, (_, index) => {
    const url = MOBILE_DUMMY_IMAGE_LIST[(index + offset) % MOBILE_DUMMY_IMAGE_LIST.length];
    return {
      id: `${album?.id || "sample"}-${index + 1}`,
      gridUrl: url,
      viewerUrl: url,
      fileName: `${album?.title || "촬영 사진"} ${index + 1}`,
      folderName: index < 6 ? "본촬영" : "추가 촬영",
    };
  });
}
